import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from "react-hook-form";
import { z } from 'zod';
import InputFloating from "@/components/atoms/InputFloating";
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import { useUpdateUserData } from '@/lib/useQuery/user/useUpdateUserData';
import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { useUserData } from "@/lib/useQuery/user/useUserData";


type Props = {
    show: boolean,
    onHide: () => void,
}

export default function BasicDataModal({
    show,
    onHide,
}: Props) {
    const { data: userData } = useUserData();

    const schema = z.object({
        first_name: z.string().min(1, { message: "First name is required" }),
        last_name: z.string().min(1, { message: "Last name is required" }),
      })
    type Inputs = z.infer<typeof schema>;
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<Inputs>({
        resolver: zodResolver(schema),
        defaultValues: {
            first_name: "",
            last_name: "",
        },
    })
    
    const onCloseQuestionModal = () => {
        reset({
            first_name: "",
            last_name: "",
        });
        onHide();
    }

    const { isPending, mutateAsync: mutateUpdateUserData } = useUpdateUserData();

    const onSubmit = async (data: {
        first_name: string,
        last_name: string,
    }) => {
        const userId = userData?.id;
        if(!userId) return;
  
        try {
            const result = await mutateUpdateUserData({...data, id: userId});
            if(result.status === 204) {
                SuccessPopup("Basic data updated successfully");
                onCloseQuestionModal();
            } else {
                ErrorPopup("An error occurred. Please try again later.");
            }
        } catch (err) {
            ErrorPopup("An error occurred. Please try again later.");
        }
    }

    const onSubmitHandler = () => {
        handleSubmit(onSubmit)();
    }

    useEffect(() => {
        if (show && userData) {
            reset({
                first_name: userData.first_name || "",
                last_name: userData.last_name || "",
            });
        }
    }, [show, userData, reset]);

    return (
        <>
            {isPending && (<LoaderWithBackdrop />)}

            <Dialog open={show} onOpenChange={onCloseQuestionModal} defaultOpen={false} >
                <DialogContent className="max-w-6xl">
                    <DialogHeader>
                        <DialogTitle className='ps-3'>
                            <div className="p-0 m-0 text-start">
                                Update Basic Data
                            </div>
                        </DialogTitle>
                    </DialogHeader>

                    <ScrollArea className="max-h-[60vh] sm:max-h-[70vh] px-3">
                        <hr className="mb-3" />

                        <div className="px-1 space-y-4">
                            <InputFloating
                                showError
                                type="text"
                                label="First Name"
                                register={register("first_name", { required: true })}
                                errorMessage={errors.first_name?.message}
                            />
                            <InputFloating
                                showError
                                type="text"
                                label="Last Name"
                                register={register("last_name", { required: true })}
                                errorMessage={errors.last_name?.message}
                            />
                        </div>
                    </ScrollArea>

                    <DialogFooter className="px-3">
                        <div className="flex items-stretch justify-end px-1 m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                            <Button size="sm" variant="secondary" type="button" onClick={onCloseQuestionModal}>Back</Button>
                            <Button size="sm" type="button" onClick={onSubmitHandler}>Next</Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
